import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2, Layers, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useUserStore } from '@/client/stores/user';
import {
  useCreateTradeableObjectMutation,
  useUpdateTradeableObjectMutation,
} from '@/client/api/promotion-pricing/tradeable-object.mutation';
import { useUpsertPriceMutation } from '@/client/api/promotion-pricing/promotion-pricing.mutation';
import {
  LIST_PRICE_PRICTID,
  DEFAULT_CCODE,
  DEFAULT_COUNTRY_ISO2,
  CCODE_COUNTRY_ISO2,
  Flag,
  TradeableObjectConfig,
} from '@/client/types/promotion-pricing';
import {
  CatalogPlan,
  asNum,
  planFeatures,
  planParameters,
} from '@/client/types/promotion-pricing/catalog';
import {
  TRADEABLE_TYPE_META,
  getTypeMeta,
} from '@/client/components/admin/Promotion/promotion-pricing/catalog-meta';

const CURRENCIES = [DEFAULT_CCODE] as const;

/** Type options for the dropdown, drawn from the curated meta (ordered). */
const TYPE_OPTIONS = Object.keys(TRADEABLE_TYPE_META)
  .map(Number)
  .sort((a, b) => getTypeMeta(a).order - getTypeMeta(b).order);

/** Audience + status flags rendered as checkboxes. */
const AUDIENCE_FLAGS = [
  { key: 'for_user', label: 'Users' },
  { key: 'for_org', label: 'Organizations' },
  { key: 'for_biz', label: 'Businesses' },
  { key: 'for_ext_link', label: 'External links' },
] as const;

const STATUS_FLAGS = [
  { key: 'active', label: 'Active' },
  { key: 'popular', label: 'Popular' },
  { key: 'featured', label: 'Featured' },
  { key: 'stackable', label: 'Stackable' },
  { key: 'upgradeable', label: 'Upgradeable' },
  { key: 'atomic', label: 'Atomic' },
  { key: 'trader_plan', label: 'Trader plan' },
  { key: 'shopper_plan', label: 'Shopper plan' },
] as const;

type FlagKey =
  | (typeof AUDIENCE_FLAGS)[number]['key']
  | (typeof STATUS_FLAGS)[number]['key'];

const emptyToUndef = (v: unknown) => (v === '' || v == null ? undefined : v);
const optionalNumber = z.preprocess(emptyToUndef, z.coerce.number().optional());

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  totid: z.coerce.number({ invalid_type_error: 'Select a type' }).int().positive('Select a type'),
  name_type: z.string().optional(),
  description: z.string().optional(),
  tier: optionalNumber,
  sub_duration: optionalNumber,
  order_by: optionalNumber,
  country_iso2: z.string().min(1, 'Country is required'),
  // create-only price
  price: z.preprocess(emptyToUndef, z.coerce.number().positive('Price must be greater than 0').optional()),
  ccode: z.string().min(1),
  // flags
  for_user: z.boolean(),
  for_org: z.boolean(),
  for_biz: z.boolean(),
  for_ext_link: z.boolean(),
  active: z.boolean(),
  popular: z.boolean(),
  featured: z.boolean(),
  stackable: z.boolean(),
  upgradeable: z.boolean(),
  atomic: z.boolean(),
  trader_plan: z.boolean(),
  shopper_plan: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

/** Clean a possibly-"None" string field for seeding. */
const cleanStr = (v: unknown): string => (v != null && v !== 'None' ? String(v) : '');
const toFlag = (v: boolean): Flag => (v ? 1 : 0);
/** Coerce a param value: numeric-looking → number (keeps -1 "unlimited"), else string. */
const coerceParamValue = (raw: string): string | number => {
  const t = raw.trim();
  if (t === '') return '';
  const n = Number(t);
  return Number.isFinite(n) && String(n) === t ? n : raw;
};

const defaultsFor = (plan: CatalogPlan | null, seedTotid?: number): FormValues => {
  if (!plan) {
    return {
      name: '',
      totid: seedTotid ?? TYPE_OPTIONS[0] ?? 1,
      name_type: '',
      description: '',
      tier: undefined,
      sub_duration: undefined,
      order_by: undefined,
      country_iso2: DEFAULT_COUNTRY_ISO2,
      price: undefined,
      ccode: DEFAULT_CCODE,
      for_user: false,
      for_org: false,
      for_biz: false,
      for_ext_link: false,
      active: true,
      popular: false,
      featured: false,
      stackable: false,
      upgradeable: false,
      atomic: false,
      trader_plan: false,
      shopper_plan: false,
    };
  }
  return {
    name: cleanStr(plan.name),
    totid: plan.totid,
    name_type: cleanStr(plan.name_type),
    description: cleanStr(plan.description),
    tier: asNum(plan.tier) ?? undefined,
    sub_duration: asNum(plan.sub_duration) ?? undefined,
    order_by: plan.order_by ?? undefined,
    country_iso2: cleanStr(plan.country_iso2) || DEFAULT_COUNTRY_ISO2,
    price: undefined,
    ccode: DEFAULT_CCODE,
    for_user: plan.for_user === 1,
    for_org: plan.for_org === 1,
    for_biz: plan.for_biz === 1,
    for_ext_link: plan.for_ext_link === 1,
    active: plan.active === 1,
    popular: plan.popular === 1,
    featured: plan.featured === 1,
    stackable: plan.stackable === 1,
    upgradeable: plan.upgradeable === 1,
    atomic: plan.atomic === 1,
    trader_plan: plan.trader_plan === 1,
    shopper_plan: plan.shopper_plan === 1,
  };
};

interface PlanConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** null = create mode; a plan = edit mode. */
  plan: CatalogPlan | null;
  /** Pre-selected type when creating from a specific group. */
  seedTotid?: number;
}

const PlanConfigModal: React.FC<PlanConfigModalProps> = ({
  isOpen,
  onClose,
  plan,
  seedTotid,
}) => {
  const isEdit = !!plan;
  const uidActor = useUserStore((s) => s.user?.uid);

  const { mutateAsync: createPlan, isPending: isCreating } = useCreateTradeableObjectMutation();
  const { mutateAsync: updatePlan, isPending: isUpdating } = useUpdateTradeableObjectMutation();
  const { mutateAsync: upsertPrice } = useUpsertPriceMutation();
  const isPending = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultsFor(plan, seedTotid),
  });

  // Dynamic editors kept outside rhf (repeatable rows).
  const [features, setFeatures] = useState<string[]>([]);
  const [params, setParams] = useState<Array<{ key: string; value: string }>>([]);

  useEffect(() => {
    if (!isOpen) return;
    reset(defaultsFor(plan, seedTotid));
    setFeatures(plan ? planFeatures(plan) : []);
    setParams(
      plan
        ? Object.entries(planParameters(plan)).map(([key, value]) => ({
            key,
            value: String(value),
          }))
        : [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, plan?.toid, seedTotid]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const buildConfig = (values: FormValues): TradeableObjectConfig => {
    const parameters: Record<string, string | number> = {};
    for (const { key, value } of params) {
      const k = key.trim();
      if (!k) continue;
      parameters[k] = coerceParamValue(value);
    }
    const description_json = features.map((f) => f.trim()).filter(Boolean);

    return {
      name: values.name.trim(),
      name_type: values.name_type?.trim() || undefined,
      description: values.description?.trim() || undefined,
      description_json: description_json.length ? description_json : undefined,
      totid: values.totid,
      tier: values.tier,
      sub_duration: values.sub_duration,
      order_by: values.order_by,
      country_iso2: values.country_iso2,
      parameters: Object.keys(parameters).length ? parameters : undefined,
      for_user: toFlag(values.for_user),
      for_org: toFlag(values.for_org),
      for_biz: toFlag(values.for_biz),
      for_ext_link: toFlag(values.for_ext_link),
      active: toFlag(values.active),
      popular: toFlag(values.popular),
      featured: toFlag(values.featured),
      stackable: toFlag(values.stackable),
      upgradeable: toFlag(values.upgradeable),
      atomic: toFlag(values.atomic),
      trader_plan: toFlag(values.trader_plan),
      shopper_plan: toFlag(values.shopper_plan),
    };
  };

  const onSubmit = handleSubmit(async (values) => {
    const config = buildConfig(values);
    try {
      if (isEdit && plan) {
        await updatePlan({ toid: plan.toid, payload: config });
        toast.success(`Plan "${config.name}" updated`, { richColors: true });
        handleClose();
        return;
      }

      const created = await createPlan(config);

      // Optionally set the initial list price on the freshly created plan.
      if (values.price != null && created?.toid != null) {
        if (uidActor == null) {
          toast.warning('Plan created, but price was skipped (no admin identity).', {
            richColors: true,
          });
        } else {
          const country_iso2 =
            CCODE_COUNTRY_ISO2[values.ccode] ?? values.country_iso2 ?? DEFAULT_COUNTRY_ISO2;
          await upsertPrice({
            prictid: LIST_PRICE_PRICTID,
            toid: created.toid,
            ccode: values.ccode,
            country_iso2,
            price: values.price,
            uid_actor: uidActor,
          });
        }
      }
      toast.success(`Plan "${config.name}" created`, { richColors: true });
      handleClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save plan', { richColors: true });
    }
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
              <Layers className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-gray-900">
                {isEdit ? 'Edit Plan Configuration' : 'Create Plan'}
              </DialogTitle>
              <DialogDescription>
                {isEdit
                  ? 'Update the configuration for this tradeable object.'
                  : 'Configure a new tradeable object and optionally set its list price.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={onSubmit} className="mt-4 space-y-6">
          {/* Basics */}
          <section className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Basics</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="name">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input id="name" placeholder="e.g. Silver Slider" {...register('name')} />
                {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="name_type">Display title</Label>
                <Input id="name_type" placeholder="Shown on the card" {...register('name_type')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="totid">
                  Type <span className="text-red-500">*</span>
                </Label>
                <select
                  id="totid"
                  {...register('totid')}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {getTypeMeta(t).label} (#{t})
                    </option>
                  ))}
                </select>
                {errors.totid && <p className="text-xs text-red-500">{errors.totid.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="country_iso2">Country</Label>
                <Input id="country_iso2" maxLength={6} {...register('country_iso2')} />
                {errors.country_iso2 && (
                  <p className="text-xs text-red-500">{errors.country_iso2.message}</p>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                rows={2}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register('description')}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="tier">Tier</Label>
                <Input id="tier" type="number" min="0" step="1" {...register('tier')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sub_duration">Duration (days)</Label>
                <Input id="sub_duration" type="number" min="0" step="1" {...register('sub_duration')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="order_by">Order</Label>
                <Input id="order_by" type="number" step="1" {...register('order_by')} />
              </div>
            </div>
          </section>

          {/* Audience */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Audience</h3>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {AUDIENCE_FLAGS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    {...register(f.key as FlagKey)}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </section>

          {/* Status flags */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Status</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
              {STATUS_FLAGS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    {...register(f.key as FlagKey)}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </section>

          {/* Feature bullets */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Feature bullets
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7"
                onClick={() => setFeatures((prev) => [...prev, ''])}
              >
                <Plus className="mr-1 h-3.5 w-3.5" /> Add
              </Button>
            </div>
            {features.length === 0 && (
              <p className="text-xs text-gray-400">No features. Add bullets shown on the plan card.</p>
            )}
            <div className="space-y-2">
              {features.map((feat, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={feat}
                    placeholder={`Feature ${i + 1}`}
                    onChange={(e) =>
                      setFeatures((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 flex-shrink-0 text-gray-400 hover:text-red-500"
                    onClick={() => setFeatures((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </section>

          {/* Parameters */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Parameters
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7"
                onClick={() => setParams((prev) => [...prev, { key: '', value: '' }])}
              >
                <Plus className="mr-1 h-3.5 w-3.5" /> Add
              </Button>
            </div>
            <p className="text-xs text-gray-400">
              Flat key/value limits (e.g. <code>max_photos</code> = <code>10</code>). Use{' '}
              <code>-1</code> for unlimited.
            </p>
            <div className="space-y-2">
              {params.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={row.key}
                    placeholder="key"
                    className="max-w-[45%]"
                    onChange={(e) =>
                      setParams((prev) =>
                        prev.map((r, idx) => (idx === i ? { ...r, key: e.target.value } : r)),
                      )
                    }
                  />
                  <Input
                    value={row.value}
                    placeholder="value"
                    onChange={(e) =>
                      setParams((prev) =>
                        prev.map((r, idx) => (idx === i ? { ...r, value: e.target.value } : r)),
                      )
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 flex-shrink-0 text-gray-400 hover:text-red-500"
                    onClick={() => setParams((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </section>

          {/* Initial price (create only) */}
          {!isEdit && (
            <section className="space-y-2 rounded-lg border border-blue-200 bg-blue-50/50 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-500">
                Initial list price (optional)
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-1.5 sm:col-span-1">
                  <Label htmlFor="ccode">Currency</Label>
                  <select
                    id="ccode"
                    {...register('ccode')}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="price">Amount</Label>
                  <Input id="price" type="number" min="0" step="0.01" placeholder="0.00" {...register('price')} />
                  {errors.price && <p className="text-xs text-red-500">{errors.price.message}</p>}
                </div>
              </div>
            </section>
          )}

          <div className="flex items-center justify-end gap-3 border-t pt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              <X className="mr-1.5 h-4 w-4" />
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : isEdit ? (
                'Save Changes'
              ) : (
                'Create Plan'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PlanConfigModal;
